import React from 'react';

/**
 * EnergyVerdictPill — compact ROI verdict chip surfaced on Capital Plan
 * deficiency rows so deferred-vs-replace decisions are visible at portfolio
 * scale, not buried inside each asset's Energy Impact card.
 *
 * verdict: 'recommended' | 'marginal' | 'not_recommended'
 *        | 'no_financial_payback' | 'unknown'  (anything else → null render)
 *
 * The verdict is derived server-side from
 *   energy_analysis.financial.recommendation
 * which compares simple_payback_years against the asset's adjusted RUL / EUL.
 */
const VERDICT_CFG = {
  recommended: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
    icon: '\u2713',
    label: 'Replace now',
    title: 'Energy ROI: Replace now — payback well under remaining useful life.',
  },
  marginal: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    icon: '~',
    label: 'Marginal',
    title: 'Energy ROI: Marginal — payback close to remaining useful life.',
  },
  not_recommended: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: '\u2715',
    label: 'Defer',
    title: 'Energy ROI: Defer — payback exceeds remaining useful life. Run-to-fail or natural replacement.',
  },
  no_financial_payback: {
    bg: 'bg-slate-50 dark:bg-slate-800',
    text: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-200 dark:border-slate-700',
    icon: '\u2299',
    label: 'Carbon-only',
    title: 'Energy ROI: No measurable financial payback — recommendation based on carbon impact only.',
  },
};

const EnergyVerdictPill = ({ verdict, reason, size = 'xs', className = '', testId }) => {
  if (!verdict) return null;
  const cfg = VERDICT_CFG[verdict];
  if (!cfg) return null;

  const sizeCls = size === 'sm'
    ? 'text-xs px-2 py-0.5 gap-1'
    : 'text-[10px] px-1.5 py-0.5 gap-0.5';

  return (
    <span
      className={`inline-flex items-center ${cfg.bg} ${cfg.text} ${cfg.border} border font-semibold rounded-full ${sizeCls} ${className}`}
      title={reason || cfg.title}
      data-testid={testId || `energy-verdict-pill-${verdict}`}
    >
      <span aria-hidden="true">{cfg.icon}</span>
      <span>{cfg.label}</span>
    </span>
  );
};

export default EnergyVerdictPill;
